import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Divider, Snackbar, Alert, FormControlLabel, Switch } from "@mui/material";
import { UserContext } from "../context/UserContext";
import {
  fetchUserFullProfileData,
  updateFullProfile,
  updateVolunteerPreferences,
  calculateSenatorsAndRep,
} from "../utils/utils";
import { getDistrictLookupErrorMessage } from "../utils/districtLookupErrors";
import { fetchVolunteerPreferences, uploadProfileImageBase64 } from "../utils/dashboardApi";
import { theme } from "../theme/colors";

import ProfileHeader from "../components/AccountDetailsComponents/ProfileHeader";
import BasicInfoSection from "../components/AccountDetailsComponents/BasicInfoSection";
import ContactLocationSection from "../components/AccountDetailsComponents/ContactLocationSection";
import SocialLinksSection from "../components/AccountDetailsComponents/SocialLinksSection";
import VolunteerPreferencesSection from "../components/AccountDetailsComponents/VolunteerPreferencesSection";
import CivixProfileSection from "../components/AccountDetailsComponents/CivixProfileSection";
import RecoveryEmailSection from "../components/AccountDetailsComponents/RecoveryEmailSection";
import SecuritySection from "../components/AccountDetailsComponents/SecuritySection";
import BlockedAccountsSection from "../components/AccountDetailsComponents/BlockedAccountsSection";
import AccountManagementSection from "../components/AccountDetailsComponents/AccountManagementSection";
import DeleteAccountSection from "../components/AccountDetailsComponents/DeleteAccountSection";
import BillingSection from "../components/AccountDetailsComponents/BillingSection";
import AccountDetailsSideNav from "../components/AccountDetailsComponents/AccountDetailsSideNav";

/** PUT body: only fields the backend persists (partial update; avoids phantom UI fields). */
function buildVolunteerPreferencesPayload(prefs) {
  if (!prefs) return null;
  return { is_interested: !!prefs.is_interested };
}

// fetchUserFullProfileData expects a setLoading callback, but this page owns its
// own `loading` state across both the profile + volunteer-prefs fetches, so we
// intentionally pass a no-op and toggle loading in loadData instead.
const noopSetLoading = () => {};

const AccountDetailsPage = () => {
  const { userId, loadUserContext } = useContext(UserContext);
  const location = useLocation();
  
  const [formData, setFormData] = useState({});
  const [volunteerPrefs, setVolunteerPrefs] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProfilePublic, setSavingProfilePublic] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [repsRecalcStatus, setRepsRecalcStatus] = useState("idle");
  const [repsRecalcErrorMessage, setRepsRecalcErrorMessage] = useState(null);
  const [calculatedReps, setCalculatedReps] = useState(null);
  // Token guards stale background promises so the latest save always wins.
  const recalcRunIdRef = useRef(0);
  const doneTimeoutRef = useRef(null);

  useEffect(() => {
    if (!userId) return;

    // Guard against stale writes if userId changes (or the component unmounts)
    // before the in-flight fetches resolve.
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      await fetchUserFullProfileData(
        (data) => { if (!cancelled) setFormData(data); },
        (err) => {
          if (!cancelled) {
            console.error("Failed to load profile:", err);
            setToast({
              open: true,
              message: "Failed to load profile.",
              severity: "error",
            });
          }
        },
        noopSetLoading
      );

      try {
        const prefs = await fetchVolunteerPreferences();
        if (!cancelled) setVolunteerPrefs(prefs);
      } catch (err) {
        console.error("Failed to load volunteer preferences:", err);
      }

      if (!cancelled) setLoading(false);
    };

    loadData();

    return () => { cancelled = true; };
  }, [userId]);

  // Scroll to a hash anchor (e.g. #billing, #security) once the page has rendered.
  // Sections fetch their own data, so the target can mount late and the page can
  // reflow after the first scroll — keep retrying, then correct once it settles.
  useEffect(() => {
    if (loading) return undefined;
    if (typeof window === "undefined") return undefined;
    const hash = location.hash?.replace("#", "");
    if (!hash) return undefined;

    let cancelled = false;
    const timers = [];
    const deadline = Date.now() + 5000;

    const scrollToAnchor = () => {
      if (cancelled) return;
      const el = document.getElementById(hash);
      if (!el) {
        if (Date.now() < deadline) timers.push(setTimeout(scrollToAnchor, 100));
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          document
            .getElementById(hash)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 700)
      );
    };

    timers.push(setTimeout(scrollToAnchor, 50));

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [loading, location.hash]);

  useEffect(() => {
    return () => {
      if (doneTimeoutRef.current) clearTimeout(doneTimeoutRef.current);
    };
  }, []);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAddressSaved = useCallback(
    (newAddress) => {
      // Optimistic update so the read-only summary reflects the new address immediately.
      setFormData((prev) => ({
        ...prev,
        street_1: newAddress.street_1,
        city: newAddress.city,
        state: newAddress.state,
        zipcode: newAddress.zipcode,
      }));

      // Bump run id; only the latest run is allowed to mutate status / refresh context.
      const runId = recalcRunIdRef.current + 1;
      recalcRunIdRef.current = runId;
      setRepsRecalcStatus("updating");
      setRepsRecalcErrorMessage(null);

      if (doneTimeoutRef.current) {
        clearTimeout(doneTimeoutRef.current);
        doneTimeoutRef.current = null;
      }

      // Fire-and-forget: recompute reps + refresh user context in the background.
      // The dashboard's MemberCards re-render off UserContext so they update once
      // loadUserContext() finishes without the user waiting on Save Address.
      Promise.resolve()
        .then(() => calculateSenatorsAndRep({ ...formData, ...newAddress }))
        .then((result) => {
          if (recalcRunIdRef.current !== runId) return;
          setCalculatedReps(result);
          setFormData((prev) => ({
            ...prev,
            representatives_name: result.representative?.name || "",
            senator_one_name: result.senator_one?.name || "",
            senator_two_name: result.senator_two?.name || "",
            district_number: result.district ?? "",
          }));
          // loadUserContext clears the skipped flag itself once the refreshed
          // context has a rep on file. Clearing it here instead would briefly leave
          // "no rep + not skipped", which ProtectedRoute treats as unfinished
          // onboarding and redirects to /setup-representative.
          return loadUserContext();
        })
        .then(() => {
          if (recalcRunIdRef.current !== runId) return;
          setRepsRecalcStatus("done");
          doneTimeoutRef.current = setTimeout(() => {
            if (recalcRunIdRef.current !== runId) return;
            setRepsRecalcStatus("idle");
            setCalculatedReps(null);
            doneTimeoutRef.current = null;
          }, 6000);
        })
        .catch((err) => {
          console.error("Background rep recalculation failed", err);
          if (recalcRunIdRef.current !== runId) return;
          setRepsRecalcErrorMessage(
            getDistrictLookupErrorMessage(err) ||
              "Could not update your representatives automatically. Try saving the address again."
          );
          setRepsRecalcStatus("error");
        });
    },
    [formData, loadUserContext]
  );

  const handleToggleProfilePublic = async (nextValue) => {
    const previous = !!formData.is_profile_public;
    setFormData((prev) => ({ ...prev, is_profile_public: nextValue }));
    setSavingProfilePublic(true);
    try {
      await updateFullProfile({ is_profile_public: nextValue });
      await loadUserContext();
      setToast({
        open: true,
        message: nextValue ? "Your profile is now public." : "Your profile is now private.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error updating profile visibility:", err);
      setFormData((prev) => ({ ...prev, is_profile_public: previous }));
      setToast({
        open: true,
        message: "Failed to update profile visibility. Please try again.",
        severity: "error",
      });
    } finally {
      setSavingProfilePublic(false);
    }
  };

  const handleVolunteerChange = (field, value) => {
    setVolunteerPrefs((prev) => ({ ...prev, [field]: value }));
  };

  const handleSocialLinksChange = (links) => {
    setFormData((prev) => ({ ...prev, social_links: links }));
  };

  const handleImageUpload = async (base64Data) => {
    try {
      setUploadingImage(true);
      
      // Extract content type if it's a data URL
      let contentType = "image/jpeg";
      if (base64Data.startsWith("data:")) {
        const matches = base64Data.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
        if (matches && matches.length > 1) {
          contentType = matches[1];
        }
      }
      
      const response = await uploadProfileImageBase64(base64Data, contentType);
      setFormData((prev) => ({ ...prev, profile_image: response.profile_image_url }));
      await loadUserContext();
      setToast({ open: true, message: "Profile photo updated!", severity: "success" });
    } catch (err) {
      console.error("Upload error:", err);
      setToast({ open: true, message: "Failed to upload photo.", severity: "error" });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Save profile data
      await updateFullProfile(formData);
      
      // Save volunteer preferences (toggle only; do not send non-model fields)
      if (volunteerPrefs) {
        const volPayload = buildVolunteerPreferencesPayload(volunteerPrefs);
        if (volPayload) {
          const updated = await updateVolunteerPreferences(volPayload);
          setVolunteerPrefs(updated);
        }
      }
      
      // Refresh global context
      await loadUserContext();
      
      setToast({ open: true, message: "Profile saved successfully!", severity: "success" });
    } catch (err) {
      console.error(err);
      setToast({ open: true, message: "Failed to save profile.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!userId) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <p className="text-slate-600">Loading user data...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: theme.primary }} />
      </Box>
    );
  }

  const isOrg = ["organization", "non_profit", "municipality"].includes(formData.account_type_name);
  const canSubscribe =
    !!formData.account_type_name &&
    !["citizen", "user"].includes(formData.account_type_name);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", pt: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          maxWidth: 1080,
          mx: "auto",
          px: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: { lg: 4 },
        }}
      >
        <AccountDetailsSideNav showBilling={canSubscribe} />

        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 800 }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: { xs: 3, sm: 5 }, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)" }}>
          <Box id="profile" sx={{ scrollMarginTop: "88px" }}>
          <ProfileHeader 
            formData={formData} 
            profileImage={formData.profile_image}
            uploadingImage={uploadingImage}
            onImageUpload={handleImageUpload}
            accountTypeName={formData.account_type_name}
            userId={userId}
            onToast={(message, severity = "success") => setToast({ open: true, message, severity })}
          />
          
          <Divider sx={{ my: 4 }} />

          <Box sx={{ mb: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.is_profile_public}
                  onChange={(e) => handleToggleProfilePublic(e.target.checked)}
                  disabled={savingProfilePublic}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>
                    Make my profile public
                    {savingProfilePublic ? "…" : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#0f172a", mt: 0.25 }}>
                    Anyone with your profile link can view it without logging in. This saves immediately.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", ml: 0 }}
            />
          </Box>
          </Box>
          
          <BasicInfoSection formData={formData} handleChange={handleChange} isOrg={isOrg} />
          
          <Divider sx={{ my: 4 }} />
          
          <ContactLocationSection
            formData={formData}
            handleChange={handleChange}
            onAddressSaved={handleAddressSaved}
            repsRecalcStatus={repsRecalcStatus}
            repsRecalcErrorMessage={repsRecalcErrorMessage}
            calculatedReps={calculatedReps}
          />
          
          <Divider sx={{ my: 4 }} />
          
          <SocialLinksSection formData={formData} onSocialLinksChange={handleSocialLinksChange} />
          
          <Divider sx={{ my: 4 }} />
          
          <VolunteerPreferencesSection volunteerPrefs={volunteerPrefs} handleVolunteerChange={handleVolunteerChange} />
          
          <Divider sx={{ my: 4 }} />
          
          <CivixProfileSection formData={formData} handleChange={handleChange} accountTypeName={formData.account_type_name} />
          
          <Divider sx={{ my: 4 }} />

          <BillingSection canSubscribe={canSubscribe} />

          <Divider sx={{ my: 4 }} />

          <Box id="recovery-email" sx={{ scrollMarginTop: "88px" }}>
            <RecoveryEmailSection currentRecoveryEmail={formData.recovery_email} />
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box id="security" sx={{ scrollMarginTop: "88px" }}>
            <SecuritySection userPhoneNumber={formData.phone_number} />
          </Box>

          <Divider sx={{ my: 4 }} />

          <BlockedAccountsSection />

          <Divider sx={{ my: 4 }} />
          
          <AccountManagementSection />

          <Box sx={{ mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSave}
              disabled={saving}
              sx={{
                background: theme.gradient,
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                py: 1.5,
                borderRadius: 2,
                "&:hover": { opacity: 0.95, background: theme.gradient },
              }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : "Save Profile"}
            </Button>
          </Box>

          <Box id="delete-account" sx={{ scrollMarginTop: "88px" }}>
            <DeleteAccountSection />
          </Box>
        </Box>
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%", fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default AccountDetailsPage;